import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Info as InfoIcon} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';

/**
 * TextArea
 *
 * @property {*} prop Prop comment.
 *
 * @class TextArea
 * @extends {Component}
 */
class TextArea extends Component {
    static propTypes = {
        className: PropTypes.string,
        info: PropTypes.string,
        label: PropTypes.string,
        placeholder: PropTypes.string,
        value: PropTypes.string,
        onChange: PropTypes.func
    }

    static defaultProps = {
        className: '',
        info: null,
        label: null,
        onChange() {},
        placeholder: null,
        value: ''
    }

    /**
     * Creates an instance of TextArea.
     *
     * @param {Object} props Component props.
     * @memberof TextArea
     */
    constructor(props) {
        super(props);

        this.state = {active: (props.value || props.placeholder)};
    }

    /**
     * Handles the focus state change of the label.
     *
     * @memberof TextArea
     */
    @autobind
    handleFocus() {
        this.setState({active: true});
    }

    /**
     * Handles the blur state change of the label.
     *
     * @memberof TextArea
     */
    @autobind
    handleBlur() {
        const {placeholder, value} = this.props;

        if (!placeholder && !value) {
            this.setState({active: false});
        }
    }

    /**
     * Handles the field content change in the input.
     *
     * @param {Event} e The onChange event.
     *
     * @memberof TextArea
     */
    @autobind
    handleChange(e) {
        const {onChange} = this.props;

        onChange(e.target.value);
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof TextArea
     */
    render() {
        const {active} = this.state;
        const {className, info, label, placeholder, value} = this.props;

        return (
            <TextAreaElement className={className}>
                <TextAreaWrapper>
                    <TextAreaField
                        placeholder={placeholder}
                        value={value}
                        onBlur={this.handleBlur}
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                    />
                    {label && (
                        <Label active={active}>{label}</Label>
                    )}
                </TextAreaWrapper>
                {info && <Info><StyledInfoIcon color="#8E8E99" />{info}</Info>}
            </TextAreaElement>
        );
    }
}

export default TextArea;

const TextAreaElement = styled.div`
    height: ${clampCss(135, 'm')};
    margin-bottom: ${clampCss(8, 'm')};
    width: 100%;

    ${MEDIA.TABLET.UP} {
        height: ${clampCss(135, 't')};
        margin-bottom: ${clampCss(8, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        height: max(135px, ${pxToVw(135, 'd')});
        margin-bottom: max(8px, ${pxToVw(8, 'd')});
    }
`;

const TextAreaWrapper = styled.div`
    border: 1px solid #C8C8D0;
    height: ${clampCss(112, 'm')};
    position: relative;
    width: 100%;

    ${MEDIA.TABLET.UP} {
        height: ${clampCss(112, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        height: max(112px, ${pxToVw(112, 'd')});
    }
`;

const TextAreaField = styled.textarea`
    ${FONTS.SEMIBOLD}
    border: none;
    color: #8E8E99;
    font-size: 16px;
    height: 100%;
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    outline: none;
    padding: ${clampCss(12.5, 'm')} ${clampCss(12.5, 'm')};
    resize: none;
    width: 100%;

    &[type="date"] {
        text-transform: uppercase;
    }
    &:focus, :active {
        outline: none;
    }

    &::placeholder, ::-ms-input-placeholder {
        color: #8E8E99;
    }

    &::-webkit-inner-spin-button {
        display: none;
    }

    &::-webkit-calendar-picker-indicator {
        display: none;
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        padding: ${clampCss(12.5, 't')} ${clampCss(12.5, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(12px, ${pxToVw(12, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(16.8px, ${pxToVw(16.8, 'd')});
        padding: max(12.5px, ${pxToVw(12.5, 'd')}) max(12.5px, ${pxToVw(12.5, 'd')});
    }
`;

const Label = styled.label`
    ${FONTS.SEMIBOLD}
    background-color: #ffffff;
    color: #8E8E99;
    font-size: ${({active}) => (active ? clampCss(8, 'm') : clampCss(12, 'm'))};
    left: 8.5px;
    letter-spacing: ${({active}) => (active ? clampCss(0.4, 'm') : clampCss(0.5, 'm'))};
    line-height: ${({active}) => (active ? clampCss(11.2, 'm') : clampCss(16.8, 'm'))};
    padding: 0 5px;
    pointer-events: none;
    position: absolute;
    top: ${({active}) => (active ? '-5px' : '12.5px')};
    transition: font-size 0.2s 0s ease-in-out,
                letter-spacing 0.2s 0s ease-in-out,
                line-height 0.2s 0s ease-in-out,
                top 0.2s 0s ease-in-out;
    will-change: font-size, letter-spacing, line-height, top;

    ${MEDIA.TABLET.UP} {
        font-size: ${({active}) => (active ? clampCss(8, 't') : clampCss(12, 't'))};
        left: ${clampCss(8.5, 't')};
        letter-spacing: ${({active}) => (active ? clampCss(0.4, 't') : clampCss(0.5, 't'))};
        line-height: ${({active}) => (active ? clampCss(11.2, 'm') : clampCss(16.8, 't'))};
        padding: 0 ${clampCss(5, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: ${({active}) => (active ? `max(8px, ${pxToVw(8, 'd')})` : `max(12px, ${pxToVw(12, 'd')})`)};
        left: max(8.5px, ${pxToVw(8.5, 'd')});
        letter-spacing: ${({active}) => (active ? `max(0.4px, ${pxToVw(0.4, 'd')})` : `max(0.5px, ${pxToVw(0.5, 'd')})`)};
        line-height: ${({active}) => (active ? `max(11.2px, ${pxToVw(11.2, 'd')})` : `max(16.8px, ${pxToVw(16.8, 'd')})`)};
        padding: 0 max(5px, ${pxToVw(5, 'd')});
    }
`;

const Info = styled.div`
    color: #8E8E99;
    font-size: ${clampCss(10, 'm')};
    height: ${clampCss(17, 'm')};
    letter-spacing: ${clampCss(0.4, 'm')};
    line-height: ${clampCss(17, 'm')};
    margin-top: ${clampCss(8, 'm')};
    padding: 0 0 0 ${clampCss(22, 'm')};
    position: relative;
    width: 100%;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(10, 't')};
        height: ${clampCss(17, 't')};
        letter-spacing: ${clampCss(0.4, 't')};
        line-height: ${clampCss(17, 't')};
        margin-top: ${clampCss(8, 't')};
        padding: 0 0 0 ${clampCss(22, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(10px, ${pxToVw(10, 'd')});
        height: max(17px, ${pxToVw(17, 'd')});
        letter-spacing: max(0.4px, ${pxToVw(0.4, 'd')});
        line-height: max(17px, ${pxToVw(17, 'd')});
        margin-top: max(8px, ${pxToVw(8, 'd')});
        padding: 0 0 0  max(22px, ${pxToVw(22, 'd')});
    }
`;

const StyledInfoIcon = styled(InfoIcon)`
    height: ${clampCss(16, 'm')};
    left: 0;
    position: absolute;
    top: 50%;
    transform: translate(0,-50%);
    width: ${clampCss(16, 'm')};

    ${MEDIA.TABLET.UP} {
        height: ${clampCss(16, 't')};
        width: ${clampCss(16, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        height: max(16px, ${pxToVw(16, 'd')});
        width: max(16px, ${pxToVw(16, 'd')});
    }
`;